import {
  Button,
  Card,
  CardContent,
  CardHeader,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  FormHelperText,
  Grid,
  MenuItem,
  TextField,
} from "@mui/material";
import { Formik } from "formik";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import * as Yup from "yup";

import DistributionNetworkApi from "../../../../../infrastructure/api/distributionNetworkApi";
import BreadcrumbsComponent from "../../../../components/shared/breadcrumbs";

const validationSchema = Yup.object().shape({
  name: Yup.string().required("نام شرکت الزامی است"),
});

const empty = {
  id: 0,
  name: "",
  shortName: "",
  province: "",
  nationalCode: "",
  address: "",
  phone: "",
  website: "",
  email: "",
  managerName: "",
  managerPhone: "",
  mehrsunCode: "",
  status: 2,
  portalReviewEnabled: false,
};

const DistributionCompanyModifyPage = () => {
  const { id } = useParams();
  const isCreate = !id || id === "create";
  const navigate = useNavigate();
  const [initial, setInitial] = useState(empty);
  const [loading, setLoading] = useState(!isCreate);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (isCreate) return;
    setLoading(true);
    DistributionNetworkApi.getCompany(id).then((res) => {
      if (res.ok && res.data) {
        const d = res.data;
        setInitial({
          ...empty,
          ...d,
          mehrsunCode: d.mehrsunCode ?? d.MehrsunCode ?? "",
          portalReviewEnabled: Boolean(
            d.portalReviewEnabled ?? d.PortalReviewEnabled
          ),
        });
      } else toast.error(res.message || "شرکت یافت نشد");
      setLoading(false);
    });
  }, [id, isCreate]);

  const onSubmit = async (values) => {
    setSaving(true);
    try {
      const res = await DistributionNetworkApi.saveCompany({
        ...values,
        id: isCreate ? 0 : Number(id),
      });
      if (res.ok) {
        toast.success(res.message || "ذخیره شد");
        navigate("/SLN/Management/DistributionCompanies");
      } else toast.error(res.message || "خطا در ذخیره");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Grid container justifyContent="center" p={4}>
        <CircularProgress />
      </Grid>
    );
  }

  return (
    <Grid container spacing={3}>
      <Grid item xs={12}>
        <BreadcrumbsComponent />
      </Grid>
      <Grid item xs={12}>
        <Card>
          <CardHeader title={isCreate ? "ایجاد شرکت توزیع" : "ویرایش شرکت توزیع"} />
          <CardContent>
            <Formik
              enableReinitialize
              initialValues={initial}
              validationSchema={validationSchema}
              onSubmit={onSubmit}
            >
              {({
                values,
                errors,
                touched,
                handleChange,
                handleSubmit,
                setFieldValue,
              }) => (
                <form onSubmit={handleSubmit}>
                  <Grid container spacing={2}>
                    {[
                      ["name", "نام شرکت", 4],
                      ["shortName", "نام کوتاه", 4],
                      ["province", "استان", 4],
                      ["nationalCode", "شناسه ملی", 4],
                      ["phone", "تلفن", 4],
                      ["email", "ایمیل", 4],
                      ["website", "وب‌سایت", 4],
                      ["managerName", "نام مدیر", 4],
                      ["managerPhone", "تلفن مدیر", 4],
                      ["mehrsunCode", "کد مهرسان (مثلاً 021)", 4],
                      ["address", "آدرس", 12],
                    ].map(([name, label, md]) => (
                      <Grid item xs={12} md={md} key={name}>
                        <TextField
                          fullWidth
                          name={name}
                          label={label}
                          value={values[name] || ""}
                          onChange={handleChange}
                          error={Boolean(touched[name] && errors[name])}
                          helperText={touched[name] && errors[name]}
                        />
                      </Grid>
                    ))}
                    <Grid item xs={12} md={4}>
                      <TextField
                        select
                        fullWidth
                        name="status"
                        label="وضعیت"
                        value={values.status}
                        onChange={handleChange}
                      >
                        <MenuItem value={1}>پیش‌نویس</MenuItem>
                        <MenuItem value={2}>فعال</MenuItem>
                        <MenuItem value={3}>غیرفعال</MenuItem>
                      </TextField>
                    </Grid>
                    <Grid item xs={12} md={8}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={Boolean(values.portalReviewEnabled)}
                            onChange={(e) =>
                              setFieldValue(
                                "portalReviewEnabled",
                                e.target.checked
                              )
                            }
                          />
                        }
                        label="استفاده از کارتابل شرکت توزیع در سولارنت (طبق توافق)"
                      />
                      <FormHelperText sx={{ mt: 0 }}>
                        فعال: پس از تأیید ناظر سولارنت، پرونده به کارتابل
                        کارشناس امور این شرکت توزیع می‌رود. غیرفعال: روال قبلی؛
                        فقط تأیید ناظر سولارنت و ادامه فرایند.
                      </FormHelperText>
                    </Grid>
                    <Grid item xs={12}>
                      <Button
                        type="submit"
                        variant="contained"
                        disabled={saving}
                        startIcon={saving ? <CircularProgress size={16} /> : null}
                      >
                        ذخیره
                      </Button>
                      <Button
                        sx={{ ml: 1 }}
                        onClick={() =>
                          navigate("/SLN/Management/DistributionCompanies")
                        }
                      >
                        انصراف
                      </Button>
                    </Grid>
                  </Grid>
                </form>
              )}
            </Formik>
          </CardContent>
        </Card>
      </Grid>
    </Grid>
  );
};

export default DistributionCompanyModifyPage;
